import React, { useState, useMemo } from 'react'
import { Modal, Empty, Button, Icon, Input, Tag } from 'antd'
import './CharacterSelectionModal.scss'

const CharacterSelectionModal = ({
  visible,
  onCancel,
  onSelect,
  characterList = [],
  selectedCharacter = [],
  currentCharacterKey,
  characterMap = {} // Add characterMap prop for legacy support
}) => {
  const [searchText, setSearchText] = useState('')

  /**
   * Check if a characterMap value is in the new format (object with name and id)
   * New format: { name: "Carl", character_id: "KXZ7X3" }
   * Legacy format: "Carl" (string)
   */
  const isNewMapFormat = mapValue => {
    return mapValue && typeof mapValue === 'object' && mapValue.name
  }

  /**
   * Get character name from characterMap (supports both formats)
   */
  const getCharacterNameFromMap = charKey => {
    const mapValue = characterMap[charKey]
    if (!mapValue) return ''

    // New format: { name: "Carl", character_id: "KXZ7X3" }
    if (isNewMapFormat(mapValue)) {
      return mapValue.name
    }

    // Legacy format: "Carl" (string)
    return mapValue
  }

  /**
   * Get character_id from characterMap (supports both formats)
   * Returns null for legacy format
   */
  const getCharacterIdFromMap = charKey => {
    const mapValue = characterMap[charKey]
    if (!mapValue) return null

    // New format: { name: "Carl", character_id: "KXZ7X3" }
    if (isNewMapFormat(mapValue)) {
      return mapValue.character_id
    }

    // Legacy format: no character_id available
    return null
  }

  /**
   * Get the mapped character_id from selectedCharacter mappings
   */
  const getMappedCharacterId = charKey => {
    const mapping = selectedCharacter.find(
      m => m.original === charKey || m.original.includes(charKey)
    )
    return mapping?.tag || null
  }

  /**
   * Check if a character is currently selected
   * Prioritizes character_id matching over name matching
   */
  const isCharacterSelected = char => {
    if (!currentCharacterKey) return false

    // Get character_id from mapping
    const mappedCharId = getMappedCharacterId(currentCharacterKey)

    // NEW FORMAT: Use character_id match (most reliable)
    if (mappedCharId && char.character_id === mappedCharId) {
      return true
    }

    // LEGACY FORMAT FALLBACK: Use name matching
    if (!mappedCharId) {
      const mapping = selectedCharacter.find(
        m =>
          m.original === currentCharacterKey ||
          m.current === currentCharacterKey
      )
      const resolvedName = mapping
        ? mapping.current.replace(/^__|__$/g, '')
        : currentCharacterKey
        ? currentCharacterKey.replace(/^__|__$/g, '')
        : null

      if (resolvedName) {
        const charsWithSameName = characterList.filter(
          c => c.name === resolvedName
        )

        // For legacy data with duplicate names: select the first match
        if (
          charsWithSameName.length > 0 &&
          char.character_id === charsWithSameName[0].character_id
        ) {
          return true
        }
      }
    }

    return false
  }

  // Filter characters by search text
  const filteredCharacters = useMemo(() => {
    const filtered = characterList.filter(char => {
      const query = searchText.toLowerCase()
      return (
        (char.name || '').toLowerCase().includes(query) ||
        (char.gender || '').toLowerCase().includes(query) ||
        (char.attributes?.original_prompt || '').toLowerCase().includes(query)
      )
    })

    // Deduplicate by character_id
    const deduped = filtered.reduce((acc, char) => {
      if (!acc.find(c => c.character_id === char.character_id)) {
        acc.push(char)
      }
      return acc
    }, [])

    // Sort so selected character appears at top
    return deduped.sort((a, b) => {
      const aSelected = isCharacterSelected(a)
      const bSelected = isCharacterSelected(b)
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      return 0
    })
  }, [characterList, searchText, currentCharacterKey, selectedCharacter])

  // Check if there are duplicate names in the character list
  const getCharacterNameCount = useMemo(() => {
    const nameCount = {}
    characterList.forEach(char => {
      nameCount[char.name] = (nameCount[char.name] || 0) + 1
    })
    return nameCount
  }, [characterList])

  const renderCharacterItem = char => {
    const attrs = char.attributes || {}
    const pic = attrs.profile_pic || ''
    const isSelected = isCharacterSelected(char)
    const hasDuplicateName = getCharacterNameCount[char.name] > 1

    return (
      <div
        key={char.character_id}
        className={`character-card-item ${isSelected ? 'is-selected' : ''}`}
      >
        {isSelected && (
          <div className="selected-badge">
            <Icon type="check" />
          </div>
        )}

        <div className="character-image-wrapper">
          {pic ? (
            <img src={pic} alt={char.name} />
          ) : (
            <div className="no-image">
              <Icon type="user" />
              <span>No Image</span>
            </div>
          )}
        </div>

        <div className="character-content">
          <div className="character-header">
            <h4 title={char.name}>{char.name}</h4>
            <div className="character-meta">
              <Tag
                color={
                  char.gender === 'male'
                    ? 'blue'
                    : char.gender === 'female'
                    ? 'magenta'
                    : 'default'
                }
              >
                {char.gender.toUpperCase()}
              </Tag>
              {hasDuplicateName && (
                <Tag color="orange" style={{ fontSize: '11px' }}>
                  ID: {char.character_id}
                </Tag>
              )}
            </div>
          </div>

          <div className="character-action">
            {isSelected ? (
              <Button type="dashed" block className="selected-btn">
                <Icon type="check-circle" /> Selected
              </Button>
            ) : (
              <Button
                type="primary"
                ghost
                block
                onClick={() => onSelect(char.name, char.character_id)}
              >
                Select
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <Modal
      maskClosable={false}
      title="Select Character Reference"
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={1000}
      centered
      wrapClassName="character-selection-modal"
    >
      <div className="character-search-wrapper">
        <Input
          placeholder="Search characters by name or gender..."
          prefix={<Icon type="search" />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
        />
      </div>

      {filteredCharacters.length > 0 ? (
        <div className="character-grid">
          {filteredCharacters.map(renderCharacterItem)}
        </div>
      ) : (
        <Empty
          description={
            searchText
              ? 'No matching characters found.'
              : 'No characters available.'
          }
        />
      )}
    </Modal>
  )
}

export default CharacterSelectionModal
